'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface ContactHeroContent {
  icon: string;
  badge: string;
  headline: string;
  description: string;
  features: Feature[];
}

interface ContactHeroProps {
  className?: string;
}

const ContactHero = ({ className = '' }: ContactHeroProps) => {
  const [content, setContent] = useState<ContactHeroContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('content_pages')
        .select('content')
        .eq('slug', 'contact_hero')
        .eq('status', 'published')
        .maybeSingle();

      if (error) throw error;
      
      if (data?.content) {
        const parsedContent = typeof data.content === 'string' 
          ? JSON.parse(data.content) 
          : data.content;
        setContent(parsedContent);
      }
    } catch (error) {
      console.error('Error loading contact hero content:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !content) {
    return (
      <section className={`bg-surface border-b border-border ${className}`}>
        <div className="mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="max-w-4xl">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/2"></div>
              <div className="h-12 bg-muted rounded w-3/4"></div>
              <div className="h-6 bg-muted rounded w-full"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`bg-surface border-b border-border ${className}`}>
      <div className="mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="max-w-4xl">
          <div className="flex items-center space-x-3 mb-6">
            <Icon name={content.icon as any} size={32} variant="solid" className="text-primary" />
            <span className="font-accent text-sm text-text-secondary uppercase tracking-wider">
              {content.badge}
            </span>
          </div>
          
          <h1 className="font-headline font-bold text-4xl lg:text-5xl text-primary mb-6 leading-tight">
            {content.headline}
          </h1>
          
          <p className="font-body text-lg text-text-secondary leading-relaxed mb-8">
            {content.description}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {content.features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-3">
                <Icon name={feature.icon as any} size={24} variant="outline" className="text-accent mt-1" />
                <div>
                  <h3 className="font-headline font-semibold text-sm text-primary mb-1">
                    {feature.title}
                  </h3>
                  <p className="font-body text-sm text-text-secondary">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactHero;